import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { IconButton, Tooltip, useMediaQuery, useTheme } from "@mui/material";
import { Toolbar, ToolbarProps } from "common";
import CheckboxGroup, {
  CheckboxGroupProps,
} from "common/components/CheckboxGroup/CheckboxGroup";
import CircleCheckboxGroup from "common/components/CheckboxGroup/CircleCheckboxGroup";
import useDictionaries from "features/dictionaries/useDictionaries";
import { availablePrograms, isProgramAvailable } from "features/programs/programApi";
import I18n from "i18n";
import { useEffect, useMemo } from "react";

export interface ProgramCheckboxSelectorProps extends Omit<
  CheckboxGroupProps,
  "options"
> {
  showMore?: boolean;
  setShowMore?: (showMore?: boolean) => void;
  wrapperProps?: ToolbarProps;
  filter?: number[];
  compact?: boolean;
  includeInactive?: boolean;
}

export default function ProgramCheckboxSelector({
  showMore,
  setShowMore,
  sx,
  wrapperProps,
  filter,
  compact,
  includeInactive = false,
  value = [],
  onChange,
  ...props
}: ProgramCheckboxSelectorProps) {
  const { dictionaries } = useDictionaries();
  const theme = useTheme();
  const smDown = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    if (includeInactive || !dictionaries.programs) {
      return;
    }
    const inactiveIds = dictionaries.programs
      .filter(program => !isProgramAvailable(program))
      .map(program => program.id);
    const nextValue = value.filter(id => !inactiveIds.includes(Number(id)));
    if (nextValue.length !== value.length) {
      onChange?.({ target: { value: nextValue } });
    }
  }, [dictionaries.programs, includeInactive, value, onChange]);

  const options = useMemo(() => {
    const mapProgram = (program: any) => {
      const active = isProgramAvailable(program);
      const label = active ? program.label : `${program.label} (inaktív)`;
      return {
        ...program,
        color: active ? program.color : theme.palette.action.disabled,
        _htmlTitle: compact ? label : null,
        label: compact ? program.code : label,
      };
    };

    const programs = includeInactive
      ? dictionaries.programs
      : availablePrograms(dictionaries.programs);
    return programs
      ?.filter(
        p =>
          showMore ||
          (isProgramAvailable(p) &&
            p.always_visible &&
            (!filter || filter.includes(p.id))) ||
          value.includes(p.id),
      )
      .map(mapProgram);
  }, [showMore, dictionaries, filter, compact, value, theme, includeInactive]);
  const Comp = compact ? CircleCheckboxGroup : CheckboxGroup;
  return (
    <Toolbar
      {...wrapperProps}
      sx={{ justifyContent: "center", ...wrapperProps?.sx }}
    >
      <Comp
        options={options}
        value={value}
        onChange={onChange}
        {...props}
        sx={{
          //minWidth: 0,
          flex: 1,
          flexWrap: "wrap",
          ...sx,
        }}
      />
      <Tooltip
        title={showMore ? I18n.t("App.showLess") : I18n.t("App.showMore")}
      >
        <IconButton
          size="small"
          onClick={() => setShowMore && setShowMore(!showMore)}
        >
          {showMore ? <ChevronLeft /> : <ChevronRight />}
        </IconButton>
      </Tooltip>
    </Toolbar>
  );
}
